import { X, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import _ from "lodash";
import { useRef, useState } from "react";
import Draggable from "react-draggable";
import { layerIdToPrettyFormat } from "../layers/layer-utils";
import { AttributesDynamicTable } from "./attributes-dynamic-table";
import { AttributesKeyValueTable } from "./attributes-key-value-table";

interface AttributesCardProps {
  show: boolean;
  attributes: { [layerId: string]: { [key: string]: string }[] };
  selectedAttribute: string;
  onSelectAttribute: (key: string) => void;
  onSelectFeature: (key: string) => void;
  onClose: () => void;
  onInfoClick: () => void;
}

const rowsKeyValue = (attributes: {
  [key: string]: string;
}): {
  attribute: string;
  value: string;
}[] => {
  return Object.keys(attributes)
    .filter((key) => attributes[key] != undefined && attributes[key] != null)
    .map((key) => {
      return {
        attribute: key,
        value: attributes[key],
      };
    });
};

export const AttributesCard = (props: AttributesCardProps) => {
  const [expanded, setExpanded] = useState(props.show);
  const initialPosition = { x: 0, y: 0 };

  const [position, setPosition] = useState(initialPosition);
  const nodeRef = useRef(null);

  const resetPosition = () => {
    setPosition(initialPosition);
  };

  const handleInfoClick = () => {
    resetPosition();
    if (!expanded) setExpanded(true);
    props.onInfoClick();
  };

  return (
    <>
      <div className="w-10 h-10 flex items-center justify-center self-end rounded-md bg-popover shadow-md">
        <Button
          variant="ghost"
          size="icon-sm"
          disabled={_.isEmpty(props.attributes)}
          onClick={handleInfoClick}
        >
          <Info className="size-4" />
        </Button>
      </div>
      {(props.show || expanded) && (
        <Draggable
          nodeRef={nodeRef}
          handle=".drag-handle"
          position={position}
          onStop={(e, data) => setPosition({ x: data.x, y: data.y })}
        >
          <div
            ref={nodeRef}
            className="w-[450px] max-h-full flex flex-col self-end fixed z-[1000]"
            style={{
              bottom: "calc(0px + 64px + 10px)",
              left: "50%",
            }}
          >
            <Card className="p-0">
              <CardHeader className="drag-handle cursor-move flex flex-row items-center justify-between">
                <CardTitle>Selected Features</CardTitle>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="close"
                  onClick={() => {
                    setExpanded(false);
                    props.onClose();
                  }}
                >
                  <X className="size-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="max-w-[450px] overflow-auto">
                  <div className="flex gap-1 mb-4 flex-row">
                    {Object.keys(props.attributes)
                      .sort()
                      .map((key) => {
                        return (
                          <Badge
                            key={key}
                            variant={
                              key == props.selectedAttribute
                                ? "default"
                                : "outline"
                            }
                            className="cursor-pointer"
                            onClick={() => props.onSelectAttribute(key)}
                          >
                            {layerIdToPrettyFormat(key)}
                          </Badge>
                        );
                      })}
                  </div>
                </div>
                {props.attributes &&
                  props.attributes[props.selectedAttribute].length === 1 && (
                    <AttributesKeyValueTable
                      rows={rowsKeyValue(
                        props.attributes[props.selectedAttribute][0]
                      )}
                    />
                  )}
                {props.attributes &&
                  props.attributes[props.selectedAttribute].length > 1 && (
                    <AttributesDynamicTable
                      attributes={props.attributes[props.selectedAttribute]}
                      onSelectFeature={props.onSelectFeature}
                    />
                  )}
              </CardContent>
            </Card>
          </div>
        </Draggable>
      )}
    </>
  );
};
